import React, { useMemo } from 'react';
import { Redirect, useRouteMatch, useLocation } from 'react-router-dom';
import _ from 'lodash';
import i18n from 'i18n';
import { L1ObjectChannel } from 'core/l1Object/L1Object';
import {
  DefaultL1ObjectManager,
  L1ObjectManager,
} from 'core/l1Object/L1ObjectManager';
import { hasFuncs } from 'core/permission/PermissionDSL';
import { Permission } from 'core/auth/Permission';
import { AddonFeatureManager } from 'core';
import { Order } from 'core/order/Order';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { TitleWithBreadcrumbs } from 'components/TitleArea/TitleWithBreadcrumbs';
import { DynamicBreadcrumb } from 'components/Breadcrumbs/DynamicBreadcrumbs';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import { StateModal } from 'containers/Common/StateModal';
import { CampaignList } from 'containers/RtbCampaigns/RtbCampaignList/CampaignList';
import { DefaultCampaignListModel } from 'containers/RtbCampaigns/RtbCampaignList/CampaignListModel';

import {
  useCreativeManageModal,
  useFetchCampaigns,
} from './L1ObjectDetailModel';
import { CreativeManagementAction } from './CampaignCreativeManagement/CreativeManagementStateFactory';
import styles from './l1ObjectDetail.module.scss';

const l1ObjectManager: L1ObjectManager = new DefaultL1ObjectManager();

const Breadcrumbs = ({ orderName }) => {
  return (
    <TitleWithBreadcrumbs
      title={i18n.t<string>('orderDetail.labels.title')}
      routes={[
        {
          path: '/orders',
          breadcrumb: i18n.t<string>('orderHome.labels.title'),
        },
        {
          path: '/orders/:orderNumber',
          breadcrumb: DynamicBreadcrumb,
          props: {
            label: orderName,
            matchParam: 'l1ObjectId',
          },
        },
      ]}
    />
  );
};

const CampaignListWithModel = ({
  order,
  addonFeatureManager,
  campaigns,
  fetchCampaigns,
}) => {
  const campaignListModel = useMemo(
    () =>
      new DefaultCampaignListModel(
        order,
        campaigns,
        addonFeatureManager,
        () => {},
        '',
        order.budgetBalance,
        [], // Pass an empty array or appropriate SelectOptions[] value
        fetchCampaigns, // Pass fetchCampaigns as refetchCampaignList
      ),
    [order, campaigns, addonFeatureManager, fetchCampaigns],
  );
  return (
    <>
      <CampaignList model={campaignListModel} />
    </>
  );
};

const CreativeManageModal = ({
  order,
  addonFeatureManager,
  campaigns,
  fetchCampaigns,
}) => {
  const { url: currentUrl } = useRouteMatch();
  const location = useLocation();
  const creativeManageModal = useCreativeManageModal(
    order,
    addonFeatureManager,
    campaigns,
    fetchCampaigns,
  );

  if (!creativeManageModal) return null;

  const query = new URLSearchParams(location.search);
  const actionParam = query.get('action');
  const modifyPermissionItem = l1ObjectManager.getModifyPermissionItem(
    L1ObjectChannel.RTB,
  );
  const permissions =
    actionParam === CreativeManagementAction.MANAGE
      ? hasFuncs(Permission.CREATIVE_READ)
      : hasFuncs(Permission.CREATIVE_READ).and(modifyPermissionItem);
  const renderRedirect = () => <Redirect to={currentUrl} />;

  return (
    <PermissionChecker
      permissionAware={permissions}
      renderOtherwise={renderRedirect}
    >
      <StateModal model={creativeManageModal} />
    </PermissionChecker>
  );
};

interface L1ObjectDetailProps {
  order: Order;
  addonFeatureManager: AddonFeatureManager;
  children: React.ReactNode;
}
export const L1ObjectDetail: React.FC<L1ObjectDetailProps> = ({
  order,
  addonFeatureManager,
  children = null,
}) => {
  const { campaignsLoading, campaigns, fetchCampaigns } =
    useFetchCampaigns(order);
  return (
    <>
      {campaignsLoading && <LoadingIndicator />}
      <Breadcrumbs orderName={_.get(order, 'projectName')} />
      <div className={styles['head-child']}>{children}</div>
      <div className={styles.list}>
        <CampaignListWithModel
          order={order}
          campaigns={campaigns}
          addonFeatureManager={addonFeatureManager}
          fetchCampaigns={fetchCampaigns}
        />
      </div>
      <CreativeManageModal
        order={order}
        addonFeatureManager={addonFeatureManager}
        campaigns={campaigns}
        fetchCampaigns={fetchCampaigns}
      />
    </>
  );
};
